import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { fetchSettings, settingsQueryKey } from '@/hooks/useSettings';
import { toCompletePiiTrainingRequest } from '@/services/piiTraining/piiTraining.mappers';
import type { PiiTrainingCompletionOutcome, PiiTrainingToastCopy } from '@/services/piiTraining/piiTraining.types';
import { piiTrainingStatusQueryOptions } from '@/services/piiTraining/usePiiTrainingStatus';
import { statusAccountId } from '@/services/statusGate/useStatusGate';
import type { PiiTrainingAnswers } from '@/types/piiTraining';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';

const completePiiTraining = async (answers: PiiTrainingAnswers): Promise<void> => {
  const response = await fetch('/api/pii-training/complete', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(toCompletePiiTrainingRequest(answers, new Date())),
  });
  if (!response.ok) throw new Error('Failed to complete training');
};

export const useCompletePiiTraining = (toastCopy: PiiTrainingToastCopy) => {
  const { user, refetch } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  // Status check after a successful POST. Only an explicit "required and not completed" blocks;
  // a failed check fails open, and the fetch also seeds the guard's cache on direct entry.
  const verifyCompletion = async (): Promise<PiiTrainingCompletionOutcome> => {
    try {
      const status = await queryClient.fetchQuery(piiTrainingStatusQueryOptions(statusAccountId(user)));
      return status.required && !status.completed ? 'incomplete' : 'verified';
    } catch {
      return 'verified';
    }
  };

  // A new user with the session tutorial pending lands on a new session; anyone else on the dashboard.
  const landingAfterTraining = async (): Promise<string> => {
    try {
      const { features } = await queryClient.fetchQuery({
        queryKey: settingsQueryKey(statusAccountId(user)),
        queryFn: fetchSettings,
        staleTime: 0,
      });
      return features.session_tutorial === 'pending' ? '/session/new' : '/';
    } catch {
      return '/';
    }
  };

  const mutation = useMutation({
    mutationFn: async (answers: PiiTrainingAnswers): Promise<PiiTrainingCompletionOutcome> => {
      await completePiiTraining(answers);
      return verifyCompletion();
    },
    onSuccess: async (outcome) => {
      if (outcome === 'incomplete') {
        toast({ title: toastCopy.errorTitle, description: toastCopy.errorDescription, variant: 'destructive' });
        return;
      }
      toast({ title: toastCopy.successTitle, description: toastCopy.successDescription });
      await refetch();
      navigate(await landingAfterTraining());
    },
    onError: (error) => {
      console.error('Error completing PII training:', error);
      toast({ title: toastCopy.errorTitle, description: toastCopy.errorDescription, variant: 'destructive' });
    },
  });

  return {
    submit: mutation.mutate,
    isPending: mutation.isPending,
    // The POST succeeded: never allow a second submission from this mount.
    isSubmitted: mutation.isSuccess,
  };
};
